import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import classNames from 'classnames'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { AuthSessionContext } from '@context/AuthContext'
import { useSessionEntity } from '@context/EntityContext'
import { useModal } from '@context/ModalsContext'
import { useProductReadContext } from '@context/ProductContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Body1 } from '@ui/Typography'

import { hasProductPendingDocumentVerificationStatus } from '@utils/AccountAccess/accountDocumentStatuses'
import { isDepositBeforeKYC, isDepositPlaced } from '@utils/AccountAccess/accountKYCStatuses'
import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { TickmillCompaniesEnum } from '@utils/companyName.utils'
import { CommonPaths } from '@utils/path'
import { isOne, isZero } from '@utils/validations'

import { isAccountDetailedActivatedStatus } from '@model/AccountDetailedStatus'
import { ClientNciPriorityDto } from '@model/ClientNciPriorityDto'
import { CreateClientDocument } from '@model/CreateClientDocuments'
import { ClientNciHistoryStatus } from '@model/CreateNciHistory'
import { DocumentCategoryType } from '@model/DocumentCategories'

import { useLandingPage } from '@hooks/useLandingPage'
import { clearLocalStorage } from '@hooks/useStorage'

import { Loading } from '@shared/components/Loading/Loading'
import { AlreadyUploadedModal } from '@shared/modals/AlreadyUploaded.modal'

import { LeftSection } from '../../Components/LeftSection'
import { SignupFormWrapper } from '../../Components/SignupFormWrapper'
import { SignupSections, SignupStep } from '../../Components/SignupStep'
import { SignupWrapper } from '../../Components/SignupWrapper'
import { DocumentInfoPageProofOfAddressStepForm } from './DocumentInfoPageProofOfAddressStepForm'
import { DocumentInfoPageStep1Page } from './DocumentInfoPageStep1/DocumentInfoPageStep1Page'
import { DocumentInfoPageStepPendingForm } from './DocumentInfoPageStepPending'

import styles from './DocumentsInfoPage.module.scss'

interface AccountNciAvailablePrioritiesProps {
  counter: { value: number; isLocked: boolean }
}

const useAccountNciAvailablePriorities = (props: AccountNciAvailablePrioritiesProps) => {
  const { counter } = props

  const locale = useSessionLanguage()
  const apiClient = useApiClient(ClientApiClient)

  const [accountNciAvailablePriorities, setAccountNciAvailablePriorities] =
    useState<ClientNciPriorityDto>()

  const { data: accountNciAvailablePrioritiesFetch, isLoading } = useQuery({
    queryKey: queryKeys.signup.nciPriorities(locale),
    queryFn: () => apiClient.getAccountNciAvailablePriorities(locale),
    enabled: !counter.isLocked,
  })

  useEffect(() => {
    if (accountNciAvailablePrioritiesFetch) {
      setAccountNciAvailablePriorities(accountNciAvailablePrioritiesFetch)
    }
  }, [accountNciAvailablePrioritiesFetch])

  return {
    accountNciAvailablePriorities,
    setAccountNciAvailablePriorities,
    isLoading,
  }
}

const useDocumentCategories = () => {
  const locale = useSessionLanguage()
  const apiClient = useApiClient(ClientApiClient)

  const { data: documentTypes } = useQuery({
    queryKey: queryKeys.signup.documentTypes(DocumentCategoryType.Address, locale),
    queryFn: () => apiClient.getDocumentCategories(DocumentCategoryType.Address, locale),
  })

  return { documentTypes }
}

export const DocumentsInfoPage: React.FC = () => {
  const { clientId } = useParams<{ clientId?: string }>()
  const navigate = useNavigate()

  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()
  const { refreshAccount } = useAccountWriteContext()
  const locale = useSessionLanguage()
  const entity = useSessionEntity()
  const { product } = useProductReadContext()

  const { t } = useTranslation()
  const [_, setAuth] = useContext(AuthSessionContext) // eslint-disable-line @typescript-eslint/no-unused-vars

  const [step, setStep] = useState(0)
  const [counter, setCounter] = useState({ value: 0, isLocked: false })
  const { getLandingPage } = useLandingPage()
  const alreadyUploadedModal = useModal(AlreadyUploadedModal)
  const { accountNciAvailablePriorities, setAccountNciAvailablePriorities, isLoading } =
    useAccountNciAvailablePriorities({ counter })

  const { documentTypes } = useDocumentCategories()

  useEffect(() => {
    setStep(hasProductPendingDocumentVerificationStatus(account, product) ? 0 : 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale])

  const handleSubmitReason = async (reason: string) => {
    await apiClient.accountAcceptNciHistory({
      statusId: ClientNciHistoryStatus.INACTIVE,
      nciPriorityId: accountNciAvailablePriorities?.id || '',
      reason,
    })
    setCounter({ value: counter.value + 1, isLocked: false })
  }

  const handleChoosePreviousDoc = async (doc: ClientNciPriorityDto, count: number) => {
    setAccountNciAvailablePriorities(doc)
    setCounter({ value: count, isLocked: true })
  }

  const handleUploadDocuments = async (documents: CreateClientDocument[]) => {
    try {
      await apiClient.uploadDocuments({ documents })
      setStep(4)
    } catch {
      alreadyUploadedModal.open()
    }
  }

  const handlePendingSubmit = async () => {
    return setStep(1)
  }

  const handleSubmit = async () => {
    setStep(2)
  }

  const handleSkip = async () => {
    if (isZero(step)) {
      return setStep(1)
    }
    if (isOne(step)) {
      return setStep(2)
    }
    if (step === 2) {
      return setStep(4)
    }
  }

  const handleSkipDocuments = async () => {
    await refreshAccount(locale)

    const shouldNavigateToTransactionHistory =
      isDepositBeforeKYC(account) && isDepositPlaced(account)

    const path = shouldNavigateToTransactionHistory
      ? '/dashboard/traders-room/transaction-history'
      : getLandingPage()

    navigate(path)
  }

  if (isAccountActivated(account) && isAccountId(account?.id, clientId)) {
    setAuth(null)
    clearLocalStorage()
    navigate('/logout')
    return null
  }

  if (step === 4) {
    navigate('/sign-up/confirm')
  }

  return (
    <Loading isLoading={isLoading}>
      <SignupFormWrapper label={t('Sign up.Sign up')} cancelPath={CommonPaths.DocumentManagement}>
        <SignupStep
          subStep={{ current: step, total: 2 }}
          accountTypeId={account?.type?.id}
          section={SignupSections.Documents}
        />
        <SignupWrapper reversed={step === 2}>
          <React.Fragment>
            <LeftSection
              title={t('Sign up.Documents')}
              description={() => (
                <React.Fragment>
                  {entity === TickmillCompaniesEnum.TICKMILL_UK ? (
                    <Body1 isParagraph>
                      {t(
                        'Sign up.We just need to verify your identity. Get your identification documents ready.'
                      )}
                    </Body1>
                  ) : (
                    <Body1 isParagraph>
                      {t(
                        'Sign up.We just need to verify your identity. Get your identification documents ready and you should complete this step in about 5 minutes.'
                      )}
                    </Body1>
                  )}
                </React.Fragment>
              )}
            />
            <section className={classNames(styles.formSection)}>
              {isZero(step) && (
                <DocumentInfoPageStepPendingForm
                  handleSubmit={handlePendingSubmit}
                  onSkip={handleSkip}
                  onSkipDocuments={handleSkipDocuments}
                />
              )}
              {accountNciAvailablePriorities && isOne(step) && (
                <DocumentInfoPageStep1Page
                  accountNciAvailablePriorities={accountNciAvailablePriorities}
                  handleSubmitReason={handleSubmitReason}
                  handleChoosePreviousDoc={handleChoosePreviousDoc}
                  handleSubmit={handleSubmit}
                  onSkip={handleSkip}
                  onSkipDocuments={handleSkipDocuments}
                />
              )}
              {step === 2 && (
                <DocumentInfoPageProofOfAddressStepForm
                  handleSubmit={handleUploadDocuments}
                  onSkip={handleSkip}
                  onSkipDocuments={handleSkipDocuments}
                  documentTypes={documentTypes}
                />
              )}
            </section>
          </React.Fragment>
        </SignupWrapper>
      </SignupFormWrapper>
    </Loading>
  )
}

const isAccountActivated = (account: any) => {
  return isAccountDetailedActivatedStatus(account?.status.id || -1)
}

const isAccountId = (accountId: any, clientId: any) => {
  return accountId !== clientId
}
